/**
 * AdminOverviewSection — surfaces platform-wide data the v7.6 SuperAdmin
 * mockup requires: user/players/schools/clubs/scouts counters, platform
 * growth chart, subscription status donut, registrations by province
 * (horizontal bar), revenue stream donut, PDS distribution histogram,
 * WhatsApp activity, managed inboxes, alerts queue.
 */
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import api from '@/utils/api';
import {
  Users, Trophy, Building2, Shield, Eye, CreditCard, DollarSign,
  MessageCircle, Mail, AlertCircle, MapPin, TrendingUp, Loader2,
} from 'lucide-react';

const Pill = ({ children, color = 'text-zinc-400 border-zinc-700' }) => (
  <span className={`text-[10px] uppercase tracking-wider font-bold border rounded-full px-2 py-0.5 ${color}`}>{children}</span>
);

function StatTile({ icon: Icon, label, value, delta, accent = '#A3FF00' }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-zinc-400 text-xs uppercase tracking-wider">{label}</p>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <p className="text-2xl font-heading font-bold text-white">{value}</p>
      {delta !== undefined && (
        <p className="text-[#A3FF00] text-xs mt-1">↑ {delta}% vs last 7 days</p>
      )}
    </Card>
  );
}

const STATUS_COLORS = ['#A3FF00', '#3b82f6', '#facc15', '#ef4444'];
const REV_COLORS = ['#A3FF00', '#3b82f6', '#facc15', '#a855f7', '#ef4444'];

export default function AdminOverviewSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get('/dashboard/admin-summary')
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return <Card className="bg-zinc-900 border-zinc-800 p-12 text-center mb-6"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></Card>;
  }
  if (!data) return null;

  const {
    total_users, users_by_role, active_subscriptions, revenue_mtd,
    platform_growth, subscription_status, registrations_by_province,
    revenue_streams, pds_distribution, platform_messaging_activity, managed_inboxes,
    alerts, deltas_pct,
  } = data;

  return (
    <div className="space-y-6 mb-8" data-testid="admin-overview">
      {/* TOP COUNTER ROW */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatTile icon={Users} label="Total Users" value={total_users.toLocaleString()} delta={deltas_pct.total_users} />
        <StatTile icon={Trophy} label="Players" value={(users_by_role.player || 0).toLocaleString()} delta={deltas_pct.players} />
        <StatTile icon={Building2} label="Schools/Clubs" value={(users_by_role.school_club || 0).toLocaleString()} delta={deltas_pct.schools} accent="#3b82f6" />
        <StatTile icon={Shield} label="Coaches" value={(users_by_role.coach || 0).toLocaleString()} delta={deltas_pct.clubs} accent="#a855f7" />
        <StatTile icon={Eye} label="Scouts" value={(users_by_role.scout || 0).toLocaleString()} delta={deltas_pct.scouts} accent="#facc15" />
        <StatTile icon={CreditCard} label="Subscriptions" value={active_subscriptions.toLocaleString()} delta={deltas_pct.active_subscriptions} accent="#3b82f6" />
        <StatTile icon={DollarSign} label="Revenue (MTD)" value={`R${revenue_mtd.toLocaleString()}`} delta={deltas_pct.revenue_mtd} />
      </div>

      {/* GROWTH + SUBSCRIPTION + ALERTS */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr] gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-white">Platform Growth</h3>
            <span className="text-zinc-500 text-xs">Last 12 months</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={platform_growth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="month" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="players" stroke="#A3FF00" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="schools" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="clubs" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="scouts" stroke="#a855f7" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="font-heading font-bold text-white mb-3">Subscription Status</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={subscription_status} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75}>
                {subscription_status.map((_, i) => (<Cell key={i} fill={STATUS_COLORS[i]} />))}
              </Pie>
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 text-xs">
            {subscription_status.map((s, i) => (
              <div key={s.name} className="flex justify-between items-center">
                <span className="flex items-center gap-2 text-zinc-400">
                  <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[i] }} />{s.name}
                </span>
                <span className="text-zinc-300 font-mono">{s.value}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-white">Alerts &amp; Actions</h3>
            <Badge className="bg-red-500/10 text-red-300 border border-red-500/30">{alerts.length}</Badge>
          </div>
          <div className="space-y-2">
            {alerts.map((a, i) => (
              <div key={a.label} className="flex items-center gap-3 bg-zinc-950 border border-zinc-800 rounded-lg p-3" data-testid={`admin-alert-${i}`}>
                <AlertCircle className={`w-4 h-4 ${a.severity === 'warn' ? 'text-yellow-400' : 'text-blue-400'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{a.label}</p>
                  <p className="text-zinc-500 text-[11px] truncate">{a.value}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* PROVINCE + REVENUE STREAMS + PLATFORM MESSAGING */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="font-heading font-bold text-white mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-[#A3FF00]" />Registrations by Province</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={registrations_by_province} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
              <XAxis type="number" stroke="#71717a" fontSize={11} />
              <YAxis type="category" dataKey="province" stroke="#71717a" fontSize={10} width={100} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8 }} />
              <Bar dataKey="count" fill="#A3FF00" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="font-heading font-bold text-white mb-3">Revenue Streams (MTD)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={revenue_streams} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75}>
                {revenue_streams.map((_, i) => (<Cell key={i} fill={REV_COLORS[i]} />))}
              </Pie>
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 text-xs">
            {revenue_streams.map((r, i) => (
              <div key={r.name} className="flex justify-between items-center">
                <span className="flex items-center gap-2 text-zinc-400">
                  <span className="w-2 h-2 rounded-full" style={{ background: REV_COLORS[i] }} />{r.name}
                </span>
                <span className="text-zinc-300 font-mono">{r.value}%</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-white flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#A3FF00]" />Platform Messaging</h3>
            <Pill>7 days</Pill>
          </div>
          <p className="text-zinc-500 text-[11px] mb-3">In-platform Squad Feed + Parent Portal + Coach messaging only — no third-party chat apps (POPIA + Children&apos;s Act safeguarding).</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Messages Sent', value: platform_messaging_activity.messages_sent.toLocaleString(), delta: platform_messaging_activity.messages_sent_delta_pct, color: '#A3FF00' },
              { label: 'Delivery Rate', value: `${platform_messaging_activity.delivery_rate_pct}%`, delta: platform_messaging_activity.delivery_rate_delta_pct, color: '#3b82f6' },
              { label: 'Replies', value: platform_messaging_activity.replies.toLocaleString(), delta: platform_messaging_activity.replies_delta_pct, color: '#a855f7' },
              { label: 'Opt-outs', value: platform_messaging_activity.opt_outs.toLocaleString(), delta: platform_messaging_activity.opt_outs_delta_pct, color: '#ef4444' },
            ].map((m) => (
              <div key={m.label} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
                <p className="text-zinc-500 text-[10px] uppercase">{m.label}</p>
                <p className="text-xl font-bold mt-1" style={{ color: m.color }}>{m.value}</p>
                <p className={`text-[11px] ${m.delta >= 0 ? 'text-[#A3FF00]' : 'text-red-400'}`}>
                  {m.delta >= 0 ? '↑' : '↓'} {Math.abs(m.delta)}%
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* PDS DISTRIBUTION + MANAGED INBOXES */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-white">PDS Score Distribution</h3>
            <Pill>Provisional</Pill>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={pds_distribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="bucket" stroke="#71717a" fontSize={9} angle={-30} textAnchor="end" height={50} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8 }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {pds_distribution.map((d, i) => {
                  const v = parseInt(d.bucket.split('-')[0], 10);
                  const c = v < 30 ? '#ef4444' : v < 50 ? '#facc15' : v < 75 ? '#3b82f6' : '#A3FF00';
                  return <Cell key={i} fill={c} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="font-heading font-bold text-white mb-3 flex items-center gap-2"><Mail className="w-4 h-4 text-[#A3FF00]" />Managed Email Inboxes</h3>
          <div className="space-y-2">
            {managed_inboxes.map((inbox) => (
              <div key={inbox.address} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-lg p-3" data-testid={`inbox-${inbox.address}`}>
                <span className="text-sm text-zinc-200 truncate">{inbox.address}</span>
                <Badge className="bg-[#A3FF00]/10 text-[#A3FF00] border-[#A3FF00]/30">{inbox.unread}</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
